import ReactDOM from "react-dom/client";
// Library styles first, so the app's own styles win in the cascade.
import "ch3-ui-lib/dist/style.css";
import "./index.css";
import messages from "./i18n/en.json";
import {IntlProvider} from "react-intl";
import {store} from "./store/store";
import {Provider} from "react-redux";
import Keycloak from "keycloak-js";
import {ReactKeycloakProvider} from "@react-keycloak/web";
import App from "./App";
import {EnvironmentProvider} from "./providers/EnvironmentProvider.tsx";
import {MatrixProvider} from "./providers/MatrixProvider.tsx";
import logger from "./services/logger";
import {resolveVariant, type RemoteAbConfig} from "./services/abTest/variant";
import {useEffect, useMemo} from "react";
import {configureAuthRuntime, notifyAccessTokenChanged} from "./services/authRuntime";
import {installWakeTokenRefresh} from "./services/authWakeRefresh";
import {sipUserAgent} from "./components/Dialer/services/sipService";
import type {Env} from "./vite-env";

// Fetch fleet-level A/B config before the first component renders so every
// log line (including SIP init) already carries the chosen variant. Failures
// are non-fatal — resolveVariant falls through to sticky/random. A 2 s abort
// timeout guards the SPA from a blank screen if nginx or the network stalls
// on an optional asset.
//
// Wrapped in an async function instead of top-level await so the compiled
// bundle stays compatible with the build target (chrome87 / safari14 / es2020).
async function fetchRemoteAbConfig(): Promise<RemoteAbConfig> {
    try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 2000);
        try {
            const resp = await fetch('/ab-config.json', {
                cache: 'no-store',
                signal: controller.signal,
            });
            if (resp.ok) {
                return (await resp.json()) as RemoteAbConfig;
            }
        } finally {
            clearTimeout(timer);
        }
    } catch {
        // ignore — fall through to local precedence
    }
    return {};
}

function Root() {
    const urlParams = new URLSearchParams(window.location.search);
    const customProvider = urlParams.get('cp');
    return (
        <EnvironmentProvider>
            {(env) => <AuthenticatedRoot env={env} customProvider={customProvider}/>}
        </EnvironmentProvider>
    );
}

function AuthenticatedRoot({
    env,
    customProvider,
}: {
    env: Env;
    customProvider: string | null;
}) {
    const keycloakClient = useMemo(() => new Keycloak({
        url: env.URL_SSO,
        realm: "phonehq",
        clientId: "web-app",
        ...(customProvider && {cp: customProvider}),
    }), [env.URL_SSO, customProvider]);

    // This must be configured before authenticated children mount, because
    // their first render may immediately start RTK Query requests.
    useMemo(() => {
        configureAuthRuntime({
            getToken: () => keycloakClient.token,
            // Let Keycloak inspect the token's exp claim first. A valid token
            // resolves to `false` without a refresh, so an endpoint-specific
            // 401 is not mistaken for an expired authentication session.
            refreshToken: () => keycloakClient.updateToken(30),
            onSessionExpired: () => (keycloakClient.token ? keycloakClient.login() : undefined),
            isTokenExpiringWithin: (seconds) => Boolean(keycloakClient.token) && keycloakClient.isTokenExpired(seconds),
            isSessionEnded: (error) => {
                const status = (error as {response?: {status?: number}} | null)?.response?.status;
                return status === 400 || status === 401 || !keycloakClient.token;
            },
        });
    }, [keycloakClient]);

    useEffect(() => installWakeTokenRefresh(), [keycloakClient]);

    return (
        <IntlProvider locale="en" messages={messages} defaultLocale="en">
            <ReactKeycloakProvider
                authClient={keycloakClient}
                initOptions={{onLoad: "login-required", checkLoginIframe: false, pkceMethod: "S256"}}
                onTokens={({token}) => {
                    notifyAccessTokenChanged();
                    if (token) {
                        sipUserAgent?.set('authorization_jwt', token);
                    }
                }}
            >
                <Provider store={store}>
                    <MatrixProvider>
                        <App/>
                    </MatrixProvider>
                </Provider>
            </ReactKeycloakProvider>
        </IntlProvider>
    );
}

const isDesignReview = import.meta.env.DEV && window.location.pathname.startsWith('/__design');

const bootApp = () => fetchRemoteAbConfig().then((remoteAbConfig) => {
    const { variant: abVariant, source: abSource } = resolveVariant(remoteAbConfig);
    logger.info('A/B variant assigned', {
        source: 'ab_test',
        event: 'ab_variant_assigned',
        variant: abVariant,
        assignmentSource: abSource,
        remoteForce: remoteAbConfig.audio?.force ?? null,
    });
    ReactDOM.createRoot(document.getElementById("root")!).render(<Root/>);
});

if (isDesignReview) {
    void import('./pages/__design/mountCallsDesignReview').then(({mountCallsDesignReview}) =>
        mountCallsDesignReview(document.getElementById("root")!),
    );
} else {
    void bootApp();
}
